import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { closeAllProblemDialogs as closeProblemCanvasDialogs } from '@gmm/problem';

import { NO_WORK } from '../types';

import {
  getGmm,
  getIsTest,
  getSnackbarMessage,
  setSnackbarMessage,
} from './globalState';
import { problemState, problemStore } from './problemStore';
import {
  CHOOSE_WORK,
  GAMES,
  NO_MODAL,
  studentAppModalState,
} from './studentAppModalStore';
import { workState } from './workStore';

export const PRACTICE_LOCK_GRACE_PERIOD_MS = 30000;
const PRACTICE_LOCK_GRACE_PERIOD_SECONDS = PRACTICE_LOCK_GRACE_PERIOD_MS / 1000;

export const PRACTICE_LOCKED_MESSAGE = 'Your teacher has locked practice.';
export const PRACTICE_LOCK_WARNING_MESSAGE = `Your teacher has locked practice. You have ${PRACTICE_LOCK_GRACE_PERIOD_SECONDS} seconds to finish this problem.`;

interface PracticeLockStoreState {
  gracePeriodEndsAt: number | undefined;
  manualPracticeLocked: boolean;
  practiceLockAvailable: boolean;
}

export interface PracticeLockStore extends PracticeLockStoreState {
  clearPracticeLock: () => void;
  setPracticeLockAvailable: (practiceLockAvailable: boolean) => void;
  setPracticeLock: (
    manualPracticeLocked: boolean,
    gracePeriodEndsAt?: number
  ) => void;
}

interface PracticeLockLifecycle {
  problemId?: string;
  problemUnsubscribe?: () => void;
  timeout?: number;
}

const practiceLockLifecycle: PracticeLockLifecycle = {};

export const practiceLockStore = create<PracticeLockStore>()(
  devtools(
    set => ({
      gracePeriodEndsAt: undefined,
      manualPracticeLocked: false,
      practiceLockAvailable: true,
      clearPracticeLock: () => {
        clearPracticeLockGracePeriod();
        clearPracticeLockSnackbar();
        set({ gracePeriodEndsAt: undefined, manualPracticeLocked: false });
      },
      setPracticeLockAvailable: practiceLockAvailable => {
        set({ practiceLockAvailable });
      },
      setPracticeLock: (manualPracticeLocked, gracePeriodEndsAt) => {
        schedulePracticeLockTimeout(
          manualPracticeLocked ? gracePeriodEndsAt : undefined
        );
        set({ gracePeriodEndsAt, manualPracticeLocked });
      },
    }),
    { name: 'Practice Lock Store' }
  )
);

export const practiceLockState = (): PracticeLockStore =>
  practiceLockStore.getState();

export const isPracticeLockEffective = (): boolean => {
  const { gracePeriodEndsAt, manualPracticeLocked } = practiceLockState();

  return isPracticeLockEffectiveForState({
    gracePeriodEndsAt,
    isExam: getIsTest(),
    manualPracticeLocked,
  });
};

export const isPracticeLockEffectiveForState = ({
  gracePeriodEndsAt,
  isExam,
  manualPracticeLocked,
}: Pick<
  PracticeLockStoreState,
  'gracePeriodEndsAt' | 'manualPracticeLocked'
> & { isExam: boolean }): boolean => {
  return (
    manualPracticeLocked &&
    !isExam &&
    (gracePeriodEndsAt === undefined || Date.now() >= gracePeriodEndsAt)
  );
};

export const setPracticeLockFromLogin = (
  manualPracticeLocked: boolean | undefined
): void => {
  const practiceLockAvailable = typeof manualPracticeLocked === 'boolean';

  practiceLockState().setPracticeLockAvailable(practiceLockAvailable);

  if (!practiceLockAvailable) {
    practiceLockState().clearPracticeLock();

    return;
  }

  if (manualPracticeLocked) {
    clearPracticeLockGracePeriod();
    lockPracticeImmediately();

    return;
  }

  practiceLockState().clearPracticeLock();
};

export const handlePracticeLockChanged = (
  manualPracticeLocked: boolean
): void => {
  if (!manualPracticeLocked) {
    practiceLockState().clearPracticeLock();

    return;
  }

  if (practiceLockState().manualPracticeLocked) return;

  clearPracticeLockGracePeriod();
  hidePracticeChoices();

  if (getIsTest() || !problemState().selectedID) {
    lockPracticeImmediately();

    return;
  }

  const problemId = problemState().selectedID;
  const gracePeriodEndsAt = Date.now() + PRACTICE_LOCK_GRACE_PERIOD_MS;

  subscribeToPracticeLockProblem(problemId);
  practiceLockState().setPracticeLock(true, gracePeriodEndsAt);
  setSnackbarMessage({
    autoHideDuration: null,
    message: PRACTICE_LOCK_WARNING_MESSAGE,
  });
};

export const applyPracticeLockChangedMessage = (
  manualPracticeLocked: unknown
): boolean => {
  if (!practiceLockState().practiceLockAvailable) return false;

  if (typeof manualPracticeLocked !== 'boolean') return false;

  const shouldRefreshOnUnlock =
    practiceLockState().manualPracticeLocked &&
    !manualPracticeLocked &&
    !getIsTest();

  handlePracticeLockChanged(manualPracticeLocked);

  return shouldRefreshOnUnlock;
};

export const finishPracticeLockGracePeriod = (): void => {
  if (practiceLockState().gracePeriodEndsAt === undefined) return;

  clearPracticeLockGracePeriod();
  lockPracticeImmediately();
};

const clearPracticeLockGracePeriod = (): void => {
  clearPracticeLockTimeout();
  clearPracticeLockProblemSubscription();
};

const clearPracticeLockTimeout = (): void => {
  if (practiceLockLifecycle.timeout === undefined) return;

  window.clearTimeout(practiceLockLifecycle.timeout);
  practiceLockLifecycle.timeout = undefined;
};

const schedulePracticeLockTimeout = (
  gracePeriodEndsAt: number | undefined
): void => {
  clearPracticeLockTimeout();

  if (gracePeriodEndsAt === undefined) return;

  practiceLockLifecycle.timeout = window.setTimeout(
    finishPracticeLockGracePeriod,
    Math.max(gracePeriodEndsAt - Date.now(), 0)
  );
};

const subscribeToPracticeLockProblem = (problemId: string): void => {
  practiceLockLifecycle.problemId = problemId;
  practiceLockLifecycle.problemUnsubscribe = problemStore.subscribe(
    (state, previousState) => {
      const selectedID = state.selectedID;

      if (selectedID === previousState.selectedID) return;

      const gracePeriodEndsAt = practiceLockState().gracePeriodEndsAt;

      if (
        gracePeriodEndsAt !== undefined &&
        selectedID &&
        selectedID !== practiceLockLifecycle.problemId
      ) {
        finishPracticeLockGracePeriod();
      }
    }
  );
};

const clearPracticeLockProblemSubscription = (): void => {
  practiceLockLifecycle.problemId = undefined;
  practiceLockLifecycle.problemUnsubscribe?.();
  practiceLockLifecycle.problemUnsubscribe = undefined;
};

const clearPracticeLockSnackbar = (): void => {
  const snackbarMessage: unknown = getSnackbarMessage();
  const message =
    typeof snackbarMessage === 'string'
      ? snackbarMessage
      : getSnackbarObjectMessage(snackbarMessage);

  if (
    message === PRACTICE_LOCK_WARNING_MESSAGE ||
    message === PRACTICE_LOCKED_MESSAGE
  ) {
    setSnackbarMessage('');
  }
};

const getSnackbarObjectMessage = (
  snackbarMessage: unknown
): string | undefined => {
  if (
    typeof snackbarMessage !== 'object' ||
    snackbarMessage === null ||
    !('message' in snackbarMessage)
  ) {
    return undefined;
  }

  const message = snackbarMessage.message;

  return typeof message === 'string' ? message : undefined;
};

const lockPracticeImmediately = (): void => {
  clearPracticeLockTimeout();
  practiceLockState().setPracticeLock(true);

  if (getIsTest()) return;

  replacePracticeWorkWithLockedState();
  studentAppModalState().closeAllProblemDialogs();
  closeProblemCanvasDialogs();
  getGmm()?.setToBlank();
  setSnackbarMessage(PRACTICE_LOCKED_MESSAGE);
};

const hidePracticeChoices = (): void => {
  if (getIsTest()) return;

  studentAppModalState().setAvailableWork([]);
  studentAppModalState().setAvailableGames([]);
};

const replacePracticeWorkWithLockedState = (): void => {
  hidePracticeChoices();
  workState().setCurrentWork(NO_WORK);

  const currentModal = studentAppModalState().currentModal;

  if (currentModal === CHOOSE_WORK || currentModal === GAMES) {
    studentAppModalState().setCurrentModal(NO_MODAL);
  }
};
